import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Spinner } from "react-bootstrap";

import {
  FixedToolbarItem,
  FlexToolbarItem,
  Page,
  Toolbar,
  TooltipProvider,
} from "@bagel-web/components";

import PlaygroundStepper, {
  PLAYGROUND_STEP_ORDER,
  PlaygroundStep,
  PlaygroundStepStatuses,
  UNTOUCHED_STEP_STATUSES,
} from "./PlaygroundStepper";
import { EMPTY_AGENT_SEED } from "./emptyAgentSeed";
import {
  agentLlmServiceName,
  agentLlmServiceParams,
  isSameSource,
  usePlaygroundDraft,
} from "./playgroundDraft";
import { hasValidFunctionNames } from "./playgroundFunctions";
import {
  MAX_CONCURRENT_RUNS,
  parseBatchCount,
  PlaygroundRunRecord,
  toRunComponentMessages,
} from "./playgroundRun";
import PlaygroundSourceStep from "./PlaygroundSourceStep";
import PlaygroundAgentConfigStep from "./PlaygroundAgentConfigStep";
import PlaygroundFunctionsStep from "./PlaygroundFunctionsStep";
import PlaygroundAgentStateStep from "./PlaygroundAgentStateStep";
import PlaygroundRunnerStep from "./PlaygroundRunnerStep";
import APIKeySelect from "../apiKey/APIKeySelect";
import { playgroundQueries, sessionQueries } from "../queries";
import { usePlaygroundCredentials } from "../session/playgroundCredentials";
import { usePlaygroundCredentialModes } from "../session/playgroundCredentialModes";
import {
  CommonAPI,
  fetchOrganizationServicesCallback,
  fetchPlaygroundSeedCallback,
  PlaygroundGenerationMode,
  PlaygroundRunPayload,
  PlaygroundSource,
  PlaygroundSourcePrefill,
  runPlaygroundCallback,
} from "../types";

// What the batch-count field starts (and resets) at.
const DEFAULT_BATCH_COUNT = "5";

/**
 * The Playground V2 page shell: persistent header
 * (title, Run shortcut — the source and credential chips are omitted for
 * now) over the five-step stepper. Step 1 (Source) is mandatory — the other
 * steps stay disabled until a seed is loaded into the draft. The steps
 * render and edit the in-memory draft; runs replay whatever it holds.
 */
const PlaygroundPage = ({
  source,
  sourcePrefill = null,
  projectId,
  onSourceChange,
  onSourceClear,
  fetchPlaygroundSeed,
  runPlayground,
  fetchOrganizationServices,
  commonAPI,
}: {
  source: PlaygroundSource | null;
  // Partial URL params (a session, or a session + agent) prefill the Source
  // step's form without triggering a seed fetch; ignored when `source` is
  // set.
  sourcePrefill?: PlaygroundSourcePrefill | null;
  // The host's active project, where runs create their sessions; null when
  // the login has none, which blocks Run.
  projectId: string | null;
  onSourceChange: (source: PlaygroundSource) => void;
  // Drop any URL-provided source params: a from-scratch start replaces the
  // loaded source outright.
  onSourceClear: () => void;
  fetchPlaygroundSeed: fetchPlaygroundSeedCallback;
  runPlayground: runPlaygroundCallback;
  // Backs the Agent Config step's Agent LLM picker.
  fetchOrganizationServices: fetchOrganizationServicesCallback;
  // Session and moment-list lookups backing the Source step's pickers.
  commonAPI: CommonAPI;
}) => {
  const [activeStep, setActiveStep] = useState<PlaygroundStep>(
    PlaygroundStep.Source
  );
  const {
    draft,
    seedDraft,
    updateComponentMessages,
    updateAgentConfig,
    deleteComponent,
    renameComponent,
    updateFunctions,
    confirmDiscard,
  } = usePlaygroundDraft();

  // Run state lives here, not in the Runner step: steps unmount on every
  // tab switch, and an in-flight run must still land in the results list.
  // It is all draft-scoped, reset when a new seed is adopted below.
  const [runs, setRuns] = useState<Array<PlaygroundRunRecord>>([]);
  // Requests in flight, whichever history they belong to: Run stays blocked
  // until they settle, so one batch never overlaps the next.
  const [outstanding, setOutstanding] = useState(0);
  const [cue, setCue] = useState<string>("");
  const [generationMode, setGenerationMode] =
    useState<PlaygroundGenerationMode>("function");
  const [jsonSchema, setJsonSchema] = useState<string>("");
  const [batchEnabled, setBatchEnabled] = useState(false);
  const [batchCount, setBatchCount] = useState(DEFAULT_BATCH_COUNT);
  const nextRunId = useRef(1);
  // Bumped whenever the history is cleared: a request from an earlier
  // generation that settles afterwards neither records its result nor
  // launches the next run of its batch.
  const runGeneration = useRef(0);
  const clearRuns = () => {
    runGeneration.current += 1;
    setRuns([]);
  };

  // The platform decides how runs authenticate: with the developer-SSO
  // flag on, runs mint under the logged-in developer and no credential UI
  // appears; otherwise an API key must be selected. The credential store
  // persists the key selection (BAGEL-414); its persisted mode is ignored.
  const credentialModes = usePlaygroundCredentialModes();
  const ssoEnabled = credentialModes.includes("sso");
  const { credentials, setCredentials } =
    usePlaygroundCredentials(credentialModes);
  const isReplayReady = ssoEnabled || !!credentials.apiKeyId;

  // The source session's details back the API-key picker (the key lookup
  // is org-scoped). Same query the Source step ran, so this is a cache hit.
  const { data: sourceSession } = useQuery({
    queryKey: sessionQueries.get(source?.sessionId ?? ""),
    queryFn: () => commonAPI.fetchSession(source!.sessionId),
    enabled: !!source && !ssoEnabled,
  });

  const handleRun = () => {
    // The Runner disables Run without an active project to create the
    // session in, with a function the run API would reject, and until the
    // batch count parses.
    if (
      !draft ||
      projectId === null ||
      (generationMode === "function" &&
        !hasValidFunctionNames(draft.seed.functions))
    ) {
      return;
    }
    const count = batchEnabled ? parseBatchCount(batchCount) : 1;
    if (count === null) {
      return;
    }
    // Every run in the batch replays the same draft snapshot.
    const agentLlm = agentLlmServiceName(draft.seed.agent_config);
    const agentLlmParams = agentLlmServiceParams(draft.seed.agent_config);
    const inputs = {
      agent_config: draft.seed.agent_config,
      // Display provenance (source, origin moment) never rides the run
      // payload.
      component_messages: toRunComponentMessages(draft.seed.component_messages),
      // Each mode ships only its own inputs; the server rejects the rest.
      // The Runner's pre-flight ensures the schema parses before Run
      // enables.
      generation_mode: generationMode,
      functions: generationMode === "function" ? draft.seed.functions : [],
      ...(generationMode === "json"
        ? { json_schema: JSON.parse(jsonSchema) as Record<string, unknown> }
        : {}),
      cue: cue || null,
      ...(ssoEnabled ? {} : { api_key_id: credentials.apiKeyId }),
    };
    // A seeded draft records its source triple as provenance; a
    // from-scratch draft has none to record.
    const payload: PlaygroundRunPayload =
      draft.source === null
        ? inputs
        : {
            ...inputs,
            source_session_id: draft.source.sessionId,
            source_agent_id: draft.source.agentId,
            source_moment_id: String(draft.source.momentId),
          };
    const startRequest = () => runPlayground(projectId, payload);
    const generation = runGeneration.current;
    const settle = (id: number, update: Partial<PlaygroundRunRecord>) =>
      setRuns((previous) =>
        previous.map((run) => (run.id === id ? { ...run, ...update } : run))
      );
    // The batch pool: each launch records a run and hands its slot on when
    // the request settles, so at most MAX_CONCURRENT_RUNS are ever
    // outstanding. A failure only settles its own record.
    let queued = count;
    const launchNext = () => {
      if (queued === 0 || runGeneration.current !== generation) {
        return;
      }
      queued -= 1;
      const id = nextRunId.current++;
      setOutstanding((current) => current + 1);
      setRuns((previous) => [
        ...previous,
        {
          id,
          startedAt: Date.now(),
          cue: cue || null,
          agentLlm,
          agentLlmParams,
          status: "running",
        },
      ]);
      // Settling after the history was cleared: no record to update, and
      // the rest of the batch stays unlaunched.
      const finish = (update: Partial<PlaygroundRunRecord>) => {
        setOutstanding((current) => current - 1);
        if (runGeneration.current !== generation) {
          return;
        }
        settle(id, update);
        launchNext();
      };
      startRequest().then(
        (result) => finish({ status: "ok", result }),
        (error) =>
          finish({
            status: "error",
            error: error instanceof Error ? error.message : "Run failed.",
          })
      );
    };
    for (let slot = 0; slot < Math.min(count, MAX_CONCURRENT_RUNS); slot++) {
      launchNext();
    }
  };

  const {
    data: seed,
    isError,
    error,
  } = useQuery({
    queryKey: source
      ? playgroundQueries.seed(
          source.sessionId,
          source.agentId,
          source.momentId
        )
      : ["playgroundSeed", "unseeded"],
    queryFn: () =>
      fetchPlaygroundSeed(source!.sessionId, source!.agentId, source!.momentId),
    enabled: !!source,
    staleTime: Infinity,
  });

  // Adopt the fetched seed into the draft store whenever the source triple
  // changes, then advance past the completed Source step. The draft — not
  // the query cache — is what the steps render from, so edits (F4/F5) never
  // fight refetches.
  useEffect(() => {
    if (source && seed && !isSameSource(draft?.source ?? null, source)) {
      seedDraft(source, seed);
      // Run state is draft-scoped: a new seed starts a fresh results list
      // and re-prefills the run inputs from the seed.
      clearRuns();
      setCue(seed.cue ?? "");
      setGenerationMode("function");
      setJsonSchema("");
      setBatchEnabled(false);
      setBatchCount(DEFAULT_BATCH_COUNT);
      setActiveStep(PlaygroundStep.AgentConfig);
    }
  }, [source, seed]);

  const handleSourceChange = (newSource: PlaygroundSource) => {
    if (isSameSource(draft?.source ?? null, newSource)) {
      // The submitted triple is already loaded: nothing to fetch, just
      // advance past the Source step.
      setActiveStep(PlaygroundStep.AgentConfig);
      return;
    }
    // Drafts are in-memory only: switching sources
    // replaces the draft, so unsaved edits need an explicit go-ahead.
    if (confirmDiscard()) {
      onSourceChange(newSource);
    }
  };

  // From scratch: adopt the canned default seed with no source behind it,
  // clearing any URL-provided source with it.
  const handleStartEmpty = () => {
    if (!confirmDiscard()) {
      return;
    }
    onSourceClear();
    seedDraft(null, EMPTY_AGENT_SEED);
    clearRuns();
    setCue("");
    setGenerationMode("function");
    setJsonSchema("");
    setBatchEnabled(false);
    setBatchCount(DEFAULT_BATCH_COUNT);
    setActiveStep(PlaygroundStep.AgentConfig);
  };

  // The Source step is complete once the draft holds the current source's
  // seed — or a sourceless from-scratch draft, as long as no source is
  // being loaded over it; until then every other step is disabled, so a
  // stale draft can never be run while its replacement's seed loads.
  const isSeeded =
    !!draft &&
    (draft.source === null
      ? source === null
      : isSameSource(draft.source, source));
  const disabledSteps = isSeeded
    ? []
    : PLAYGROUND_STEP_ORDER.filter((step) => step !== PlaygroundStep.Source);
  const stepStatuses: PlaygroundStepStatuses =
    draft?.stepStatuses ?? UNTOUCHED_STEP_STATUSES;

  let sourcePane: React.ReactElement;
  if (isError) {
    sourcePane = (
      <Alert variant="warning" data-testid="playground-seed-error">
        Could not load the Playground seed:{" "}
        {error instanceof Error ? error.message : "unknown error"}
      </Alert>
    );
  } else if (source && !isSeeded) {
    // Pending, or the seed arrived but the draft has not adopted it yet.
    sourcePane = <Spinner data-testid="playground-seed-spinner" />;
  } else {
    const initialSource = source ?? sourcePrefill;
    sourcePane = (
      <PlaygroundSourceStep
        // Remount when the source (or prefill) changes so the form
        // re-prefills.
        key={
          initialSource
            ? `${initialSource.sessionId ?? ""}/${initialSource.agentId ?? ""}/${initialSource.momentId ?? ""}`
            : "unseeded"
        }
        initialSource={initialSource}
        commonAPI={commonAPI}
        onLoad={handleSourceChange}
        onStartEmpty={handleStartEmpty}
      />
    );
  }

  return (
    <Page title="Playground">
      <Page.Body>
        <Toolbar>
          <FixedToolbarItem>
            <h2>Playground</h2>
          </FixedToolbarItem>
          <FlexToolbarItem />
          {/* The shortcut is redundant on the Runner step itself, where the
              real Run button lives, and premature on Source, where no draft
              is loaded yet. */}
          {activeStep !== PlaygroundStep.Runner &&
            activeStep !== PlaygroundStep.Source && (
              <FixedToolbarItem>
                <TooltipProvider tooltip="Jump to the Runner step.">
                  <Button
                    variant="secondary"
                    disabled={!isSeeded}
                    onClick={() => setActiveStep(PlaygroundStep.Runner)}
                    data-testid="playground-run-shortcut"
                  >
                    <i className="bi-play-fill me-1" />
                    Run
                  </Button>
                </TooltipProvider>
              </FixedToolbarItem>
            )}
        </Toolbar>
        <PlaygroundStepper
          activeStep={activeStep}
          onSelectStep={setActiveStep}
          stepStatuses={stepStatuses}
          disabledSteps={disabledSteps}
        />
        <div className="mt-3">
          {activeStep === PlaygroundStep.Source && sourcePane}
          {isSeeded && (
            <>
              {activeStep === PlaygroundStep.AgentConfig && (
                <PlaygroundAgentConfigStep
                  agentConfig={draft.seed.agent_config}
                  fetchOrganizationServices={fetchOrganizationServices}
                  onAgentConfigChange={updateAgentConfig}
                  onComponentDelete={deleteComponent}
                  onComponentRename={renameComponent}
                />
              )}
              {activeStep === PlaygroundStep.Functions && (
                <PlaygroundFunctionsStep
                  functions={draft.seed.functions}
                  onFunctionsChange={updateFunctions}
                />
              )}
              {activeStep === PlaygroundStep.AgentState && (
                <PlaygroundAgentStateStep
                  agentConfig={draft.seed.agent_config}
                  componentMessages={draft.seed.component_messages}
                  unsupportedComponents={draft.seed.unsupported_components}
                  source={draft.source}
                  onComponentMessagesChange={updateComponentMessages}
                />
              )}
              {activeStep === PlaygroundStep.Runner && (
                <PlaygroundRunnerStep
                  functions={draft.seed.functions}
                  cue={cue}
                  onCueChange={setCue}
                  generationMode={generationMode}
                  onGenerationModeChange={setGenerationMode}
                  jsonSchema={jsonSchema}
                  onJsonSchemaChange={setJsonSchema}
                  batchEnabled={batchEnabled}
                  onBatchEnabledChange={setBatchEnabled}
                  batchCount={batchCount}
                  onBatchCountChange={setBatchCount}
                  runs={runs}
                  isRunning={outstanding > 0}
                  isReplayReady={isReplayReady}
                  needsProject={projectId === null}
                  apiKeySelect={
                    ssoEnabled ? null : (
                      <APIKeySelect
                        value={credentials.apiKeyId}
                        placeholder="Select an API key…"
                        fetchAPIKeys={({ notes }) =>
                          commonAPI.fetchAPIKeys({
                            startsAfter: null,
                            notes,
                            // The dashboard's key lookup is scoped by the
                            // login, not this value; a from-scratch draft
                            // has no source session to read it from.
                            organization: sourceSession?.organization ?? "",
                          })
                        }
                        onChange={(event) =>
                          setCredentials({ apiKeyId: event.target.value })
                        }
                        // The persisted id outlives the key: drop a revoked
                        // selection so Run cannot stay enabled on a dead
                        // credential.
                        onValueUnavailable={() =>
                          setCredentials({ apiKeyId: "" })
                        }
                      />
                    )
                  }
                  onRun={handleRun}
                />
              )}
            </>
          )}
        </div>
      </Page.Body>
    </Page>
  );
};

export default PlaygroundPage;
